import { persistAuthTokenFromResponse } from '@/lib/auth/persist-auth-token';
import { signInAfterAccountCreation } from '@/lib/auth/sign-in-after-signup';
import { getAuthToken } from '@/lib/auth/auth-token';
import { normalizeEmail } from '@/lib/helpers/normalize-email';
import { axiosInstance } from '@/lib/utils';
import type {
  UserCreationPayload,
  UserUpdatePayload,
  RequestPhoneUpdatePayload,
  ConfirmPhoneUpdatePayload,
  RequestEmailUpdatePayload,
  ConfirmEmailUpdatePayload,
  RequestSettlementAccountPayload,
  ConfirmSettlementAccountPayload,
  RequestSettlementAccountData,
} from './payload';
import type { UserResponse } from './response';
import { endpoints } from '@/api';
import type { BusinessSettlementAccount, RequestResponse } from '@/types';

export const createUser = async (payload: UserCreationPayload) => {
  const { referrerCode, ...rest } = payload;
  const response = await axiosInstance.post<UserResponse>(endpoints.user, {
    ...rest,
    email: normalizeEmail(payload.email),
    ...(referrerCode?.trim() ? { referrerCode: referrerCode.trim() } : {}),
  });
  persistAuthTokenFromResponse(response.data);

  if (!getAuthToken()) {
    await signInAfterAccountCreation({
      email: payload.email,
      password: payload.password,
      isGoogleSignup: payload.isGoogleSignup,
    });
  }

  return response.data;
};

export const updateUser = async (id: string, payload: UserUpdatePayload) => {
  const response = await axiosInstance.patch<UserResponse>(
    `${endpoints.user}/${id}`,
    payload
  );
  return response.data;
};

export const requestPhoneUpdate = async (
  payload: RequestPhoneUpdatePayload
) => {
  const response = await axiosInstance.post<RequestResponse>(
    endpoints.updatePhoneRequest,
    payload
  );
  return response.data;
};

export const confirmPhoneUpdate = async (
  payload: ConfirmPhoneUpdatePayload
) => {
  const response = await axiosInstance.post<RequestResponse>(
    endpoints.confirmPhoneUpdate,
    payload
  );
  return response.data;
};

export const requestEmailUpdate = async (
  payload: RequestEmailUpdatePayload
) => {
  const response = await axiosInstance.post<RequestResponse>(
    endpoints.updateEmailRequest,
    payload
  );
  return response.data;
};

export const confirmEmailUpdate = async (
  payload: ConfirmEmailUpdatePayload
) => {
  const response = await axiosInstance.post<RequestResponse>(
    endpoints.confirmEmailUpdate,
    payload
  );
  return response.data;
};

export const requestSettlementAccountUpdate = async (
  payload: RequestSettlementAccountPayload
) => {
  const response = await axiosInstance.post<
    RequestResponse<RequestSettlementAccountData>
  >(endpoints.settlementAccountRequest, payload);
  return response.data;
};

export const confirmSettlementAccountUpdate = async (
  payload: ConfirmSettlementAccountPayload
) => {
  const response = await axiosInstance.post<
    RequestResponse<BusinessSettlementAccount>
  >(endpoints.settlementAccountConfirm, payload);
  return response.data;
};
